import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AnalysisDetail as AnalysisDetailDto } from '@swg/contracts';
import { canRunAnalyses, isRunActive, stepIndexForStatus } from '@swg/domain';
import { deleteAnalysis, fetchAnalysis, rerunAnalysis, restartStep } from '../api.js';
import { ProfileReview } from '../components/ProfileReview.js';
import { BrancheInsight } from '../components/BrancheInsight.js';
import { CompetitorSelection } from '../components/CompetitorSelection.js';
import { CollectionProgress } from '../components/CollectionProgress.js';
import { NicheOnInput } from '../components/Niche.js';
import { ReportView } from '../components/ReportView.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Stepper } from '../components/Stepper.js';
import { theme } from '../theme.js';
import { useUser } from '../user-context.js';

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

export function AnalysisDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const mayRun = canRunAnalyses(user.role);

  const [reload, setReload] = useState(0);
  const [rerunning, setRerunning] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // null = "volg de verst bereikte stap"; een getal = handmatig gekozen stap.
  const [viewStep, setViewStep] = useState<number | null>(null);
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ready'; analysis: AnalysisDetailDto } | { status: 'error' }
  >({ status: 'loading' });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const analysis = await fetchAnalysis(id);
        if (cancelled) return;
        setState({ status: 'ready', analysis });
        if (analysis.latestRun && isRunActive(analysis.latestRun.status)) {
          timer = setTimeout(tick, 3000);
        }
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, reload]);

  if (state.status === 'loading') return <p style={{ color: theme.color.muted }}>Laden…</p>;
  if (state.status === 'error')
    return <p style={{ color: theme.color.muted }}>De analyse kon niet worden geladen.</p>;

  const { analysis } = state;
  const run = analysis.latestRun;
  const status = run?.status ?? 'CREATED';
  const runActive = isRunActive(status);
  const current = stepIndexForStatus(status);
  const step = viewStep ?? current;

  const refresh = () => {
    setReload((x) => x + 1);
    setViewStep(null); // volg weer de nieuwste stap
  };

  const handleRerun = async () => {
    if (rerunning) return;
    setRerunning(true);
    try {
      await rerunAnalysis(analysis.id);
      refresh();
    } catch {
      window.alert('Opnieuw uitvoeren is mislukt. Probeer het opnieuw.');
    } finally {
      setRerunning(false);
    }
  };

  const handleRestart = async (which: 'profiel' | 'branche' | 'concurrenten' | 'verzamelen') => {
    if (restarting) return;
    setRestarting(true);
    try {
      await restartStep(analysis.id, which);
      refresh();
    } catch {
      window.alert('Deze stap opnieuw starten is mislukt. Probeer het opnieuw.');
    } finally {
      setRestarting(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    if (!window.confirm(`Analyse "${analysis.title}" en alle uitvoeringen definitief verwijderen?`)) {
      return;
    }
    setDeleting(true);
    try {
      await deleteAnalysis(analysis.id);
      navigate('/');
    } catch {
      window.alert('Verwijderen is mislukt. Probeer het opnieuw.');
      setDeleting(false);
    }
  };

  const restartBtn = (which: 'profiel' | 'branche' | 'concurrenten' | 'verzamelen', label: string) =>
    mayRun ? (
      <button
        type="button"
        onClick={() => void handleRestart(which)}
        disabled={restarting}
        title="Alleen deze stap opnieuw uitvoeren"
        style={{
          background: 'transparent',
          border: `1px solid ${theme.color.borderStrong}`,
          borderRadius: 8,
          padding: '6px 12px',
          fontSize: 12.5,
          fontWeight: 600,
          color: theme.color.text,
          cursor: restarting ? 'default' : 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
        }}
      >
        {restarting ? 'Bezig…' : label}
      </button>
    ) : null;

  const toolbar = (children: React.ReactNode) =>
    children ? (
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
        {children}
      </div>
    ) : null;

  const cleanUrl = analysis.company.normalizedDomain;

  return (
    <div style={{ maxWidth: 1300, margin: '0 auto' }}>
      <Link to="/" style={{ fontSize: 13, color: theme.color.muted }}>
        ← Terug naar overzicht
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 4px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{analysis.title}</h1>
        {run && <StatusBadge status={run.status} />}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {mayRun && (
            <button
              type="button"
              onClick={() => void handleRerun()}
              disabled={rerunning}
              title="De volledige analyse opnieuw uitvoeren (kan op elk moment)"
              style={{
                background: theme.color.cta,
                color: theme.color.ctaText,
                border: 'none',
                borderRadius: 8,
                padding: '7px 14px',
                fontSize: 13,
                fontWeight: 700,
                cursor: rerunning ? 'default' : 'pointer',
                opacity: rerunning ? 0.7 : 1,
                fontFamily: 'inherit',
              }}
            >
              {rerunning ? 'Bezig…' : '↻ Volledig opnieuw'}
            </button>
          )}
          {mayRun && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              style={{
                border: `1px solid ${theme.color.border}`,
                background: 'transparent',
                color: theme.color.warnText,
                borderRadius: 8,
                padding: '7px 12px',
                fontSize: 13,
                fontWeight: 600,
                cursor: deleting ? 'not-allowed' : 'pointer',
              }}
            >
              {deleting ? 'Verwijderen…' : '🗑'}
            </button>
          )}
        </div>
      </div>
      <p style={{ margin: '0 0 20px', fontSize: 13.5, color: theme.color.muted }}>
        <a href={analysis.company.primaryUrl} target="_blank" rel="noreferrer">
          {analysis.company.normalizedDomain}
        </a>{' '}
        · aangemaakt {formatDate(analysis.createdAt)}
      </p>

      {run && <Stepper current={current} viewStep={step} onNavigate={(i) => setViewStep(i)} />}

      {/* ── Stap 0: Invoer / voorbereiden ── */}
      {step === 0 &&
        (['CREATED', 'PROFILE_QUEUED', 'PROFILE_RUNNING'].includes(status) ? (
          <div style={{ maxWidth: 560, margin: '40px auto 0', textAlign: 'center' }}>
            <div
              aria-label="Laden"
              style={{
                width: 44,
                height: 44,
                border: `4px solid ${theme.color.border}`,
                borderTopColor: theme.color.primary,
                borderRadius: '50%',
                margin: '0 auto 22px',
                animation: 'swg-spin 0.9s linear infinite',
              }}
            />
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600 }}>Analyse wordt voorbereid</h2>
            <div style={{ fontSize: 14, color: theme.color.muted }}>
              De website wordt herkend en het bedrijfsprofiel wordt opgesteld voor
            </div>
            <div style={{ marginTop: 8, fontFamily: theme.font.mono, fontSize: 14, color: theme.color.primary }}>
              {cleanUrl}
            </div>
          </div>
        ) : (
          <div
            style={{
              background: theme.color.surface,
              border: `1px solid ${theme.color.border}`,
              borderRadius: 14,
              padding: '22px 26px',
            }}
          >
            <h2 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Ingevoerde website</h2>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: theme.color.muted }}>
              De analyse is gestart vanaf deze openbare bedrijfswebsite.
            </p>
            <a
              href={analysis.company.primaryUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontFamily: theme.font.mono, fontSize: 14, color: theme.color.primary }}
            >
              {analysis.company.primaryUrl}
            </a>
            {/* P22 — de niche hoort hier omdat ze uit deze ene invoer volgt en
                de rest van de analyse stuurt: de branchevergelijking én het
                zoeken naar concurrenten. Toont zichzelf pas als ze er is. */}
            {run && <NicheOnInput key={`niche-${run.id}-${run.status}`} runId={run.id} />}
          </div>
        ))}

      {/* ── Stap 1: Bedrijfsprofiel ── */}
      {step === 1 && run && (
        <>
          {toolbar(status === 'PROFILE_REVIEW_REQUIRED' && restartBtn('profiel', '↻ Profiel opnieuw opstellen'))}
          <ProfileReview
            key={`profile-${run.id}-${run.status}`}
            runId={run.id}
            runActive={runActive}
            canEdit={mayRun}
            onConfirmed={refresh}
            onBack={() => setViewStep(0)}
          />
        </>
      )}

      {/* ── Stap 2: Branche (CBS-branchestatistieken, C09) ── */}
      {step === 2 && run && (
        <>
          {toolbar(!runActive && current >= 2 && restartBtn('branche', '↻ Branchegegevens opnieuw ophalen'))}
          <BrancheInsight
            key={`branche-${run.id}-${run.status}`}
            runId={run.id}
            runActive={current === 2 && runActive}
            canEdit={mayRun}
            onConfirmed={refresh}
            onBack={() => setViewStep(1)}
          />
        </>
      )}

      {/* ── Stap 3: Concurrenten ── */}
      {step === 3 && run && (
        <>
          {toolbar(!runActive && current >= 3 && restartBtn('concurrenten', '↻ Concurrenten opnieuw zoeken'))}
          <CompetitorSelection
            key={`comp-${run.id}-${run.status}`}
            runId={run.id}
            runActive={runActive}
            canEdit={mayRun}
            onChanged={refresh}
          />
        </>
      )}

      {/* ── Stap 4: Gegevens verzamelen ── */}
      {step === 4 && run && (
        <>
          {toolbar(!runActive && current >= 4 && restartBtn('verzamelen', '↻ Gegevens opnieuw verzamelen'))}
          {status === 'FAILED' ? (
            <div
              style={{
                background: '#FBE7E3',
                border: '1px solid #F2C6BE',
                color: '#A33B2E',
                borderRadius: 12,
                padding: '18px 22px',
                fontSize: 13.5,
                lineHeight: 1.5,
              }}
            >
              <strong>De uitvoering is mislukt tijdens het verzamelen.</strong> Je kunt alleen deze
              stap opnieuw proberen, of met “Volledig opnieuw” de hele analyse herstarten.
            </div>
          ) : (
            <CollectionProgress
              runId={run.id}
              active={current === 4 && runActive}
              onOpenReport={() => setViewStep(null)}
            />
          )}
        </>
      )}

      {/* ── Stap 5: Rapport ── */}
      {step === 5 && run && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, margin: '0 0 14px', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 21, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Concurrentierapport
            </h1>
            <span style={{ fontSize: 12.5, color: theme.color.muted }}>
              uitvoering #{run.runNumber} · {formatDate(run.finishedAt ?? run.createdAt)}
            </span>
          </div>
          <ReportView key={`report-${run.id}-${run.status}`} runId={run.id} />

          {analysis.runs.length > 1 && (
            <div
              style={{
                background: theme.color.surface,
                border: `1px solid ${theme.color.border}`,
                borderRadius: 14,
                padding: '18px 22px',
                marginTop: 4,
              }}
            >
              <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Eerdere uitvoeringen</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {analysis.runs.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: `1px solid ${theme.color.border}`,
                      borderRadius: 10,
                      padding: '10px 14px',
                      fontSize: 13,
                    }}
                  >
                    <span>
                      Uitvoering #{r.runNumber} · {formatDate(r.createdAt)}
                    </span>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
